import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import CTA from "@/components/CTA";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
  {
    question: "Is CallChat a real person or an AI?",
    answer: "CallChat is powered by advanced artificial intelligence. However, our AI is designed to sound natural and human-like. Most callers won't realize they're speaking with an AI. The technology uses natural language processing to understand context, handle interruptions, and respond appropriately—just like a well-trained receptionist.",
  },
  {
    question: "Which languages does CallChat support?",
    answer: "CallChat currently supports three languages: Dutch (Nederlands), French (Français), and English. The AI automatically detects the language the caller is using and responds in the same language. This makes it perfect for businesses in Belgium and other multilingual regions.",
  },
  {
    question: "Can CallChat integrate with my calendar?",
    answer: "Yes! CallChat integrates seamlessly with popular calendar systems including Google Calendar, Microsoft Outlook, and Apple Calendar. When a customer wants to book an appointment, CallChat checks your real-time availability and prevents double-bookings. Confirmed appointments are automatically added to your calendar.",
  },
  {
    question: "Is setup complicated? Do I need technical skills?",
    answer: "Not at all. Setting up CallChat takes about 5-10 minutes and requires no technical knowledge. Simply connect your phone number, tell us about your business (services, hours, common questions), and you're ready to go. Our intuitive dashboard guides you through every step.",
  },
  {
    question: "Is CallChat GDPR compliant?",
    answer: "Absolutely. CallChat is fully GDPR compliant. All data is stored on EU servers, encrypted with 256-bit encryption, and never shared with third parties. You have full control over data retention and can delete call recordings at any time. We also provide data processing agreements for business customers.",
  },
  {
    question: "What happens if CallChat can't answer a question?",
    answer: "If a caller asks something outside CallChat's training, it will politely explain that it doesn't have that information and offer to take a message or transfer the call to your team. You can also configure fallback behaviors like forwarding to a specific number during business hours.",
  },
  {
    question: "How does pricing work?",
    answer: "We offer three plans based on your call volume: Starter (€49/month for up to 100 calls), Pro (€99/month for up to 500 calls), and Business (custom pricing for unlimited calls). All plans include a 14-day free trial. There are no setup fees, and you can cancel anytime.",
  },
  {
    question: "Can I customize the greeting and responses?",
    answer: "Yes! You can fully customize how CallChat greets callers, answers common questions, and handles specific scenarios. You can define your brand voice, create custom FAQ responses, and set different greetings for business hours vs. after-hours calls.",
  },
  {
    question: "How quickly can CallChat be set up?",
    answer: "Most businesses are up and running within 10-15 minutes. The basic setup involves three steps: connecting your phone number, entering your business information, and customizing your preferences. More complex configurations (like custom integrations) may take longer.",
  },
  {
    question: "Do I need to change my phone number?",
    answer: "No, you don't need to change your business phone number. CallChat works with call forwarding—you simply redirect incoming calls to your CallChat number. Your customers will continue calling the same number they always have.",
  },
];

const FAQ = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Frequently Asked <span className="text-gradient">Questions</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Got questions? We've got answers. If you can't find what you're looking for, feel free to contact us.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-card border border-border rounded-xl px-6 data-[state=open]:border-primary/30 transition-colors"
                >
                  <AccordionTrigger className="text-left font-semibold hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <CTA />
      <Footer />
    </div>
  );
};

export default FAQ;
